import {
  TrackingV2GetOrderQuery,
  WebsiteInitialData_CachedQuery
} from '@data/__generated__/types.main'
import getDistanceFromLatLonInKm from '@helpers/misc/coordinatesDistance'

import buildDriverAnnotation from './buildDriverAnnotation'
import calculateDriverRotation from './calculateDriverRotation'
import {SHORT_RANGE_DISTANCE_THRESHOLD, STROKE_COLOR} from './constants'
import getCurvedLineOverlays from './getCurvedLineOverlays'
import getRoute from './getRoute'

export interface GetAnnotationsResult {
  annotations: any[]
  type: 'shortRange' | 'longRange'
  overlays: any[]
}

let cachedDriverAnnotation = null

/**
 * Returns the annotations to display on the map
 * It has two modes depending on the distance between the driver and the customer.
 * If the distance is long, it will display an arched line between the store and the customer.
 * If the distance is short, it will display the route between the driver and the customer.
 */
export default function getAnnotations(
  mapkit: any,
  order: TrackingV2GetOrderQuery['order'],
  website: WebsiteInitialData_CachedQuery['website'],
  driverLocationWithTransition: {lat: number; lng: number},
  driverLocation: {lat: number; lng: number},
  {forceShortRange}: {forceShortRange?: boolean} = {},
  onAddOverlays: (overlays: any[]) => void = () => {}
): GetAnnotationsResult {
  const {store, address} = order

  if (!driverLocation?.lat || !driverLocation?.lng)
    return getLongRangeAnnotations(mapkit, website.name, store?.address?.location, address)

  const homeLocation = order?.address?.location
  if (!homeLocation?.lat || !homeLocation?.lng)
    return {annotations: [], type: 'longRange', overlays: []}

  const driverDistance = getDistanceFromLatLonInKm(
    driverLocation.lat,
    driverLocation.lng,
    homeLocation.lat,
    homeLocation.lng
  )

  if (driverDistance < SHORT_RANGE_DISTANCE_THRESHOLD || forceShortRange) {
    return getShortRangeAnnotations(
      mapkit,
      driverLocationWithTransition,
      driverLocation,
      address,
      onAddOverlays
    )
  }

  return getLongRangeAnnotations(mapkit, website.name, store?.address?.location, address)
}

const getHomeAnnotation = (mapkit: any, address: TrackingV2GetOrderQuery['order']['address']) => {
  return new mapkit.MarkerAnnotation(
    new mapkit.Coordinate(address.location.lat, address.location.lng),
    {
      title: address.address,
      color: '#fff',
      glyphColor: '#0165ff',
      glyphImage: {
        1: '/images/icons/home_map.svg'
      }
    }
  )
}

const getLongRangeAnnotations = (
  mapkit: any,
  websiteName: string,
  storeLocation: {lat?: number; lng?: number},
  address: TrackingV2GetOrderQuery['order']['address']
): GetAnnotationsResult => {
  const annotations = []
  const overlays = []

  if (storeLocation?.lat && storeLocation?.lng) {
    annotations.push(
      new mapkit.MarkerAnnotation(new mapkit.Coordinate(storeLocation.lat, storeLocation.lng), {
        title: websiteName,
        color: '#000000',
        glyphColor: '#fff',
        glyphImage: {
          1: '/images/icons/map_store.svg'
        }
      })
    )
  }

  if (address?.location?.lat && address?.location?.lng) {
    annotations.push(getHomeAnnotation(mapkit, address))
  }

  if (annotations.length === 2) {
    overlays.push(
      ...getCurvedLineOverlays(mapkit, storeLocation, address.location, {
        color: STROKE_COLOR,
        width: 5
      })
    )
  }

  return {annotations, type: 'longRange', overlays}
}

const getShortRangeAnnotations = (
  mapkit: any,
  driverLocationWithTransition: {lat: number; lng: number},
  driverLocation: {lat: number; lng: number},
  address: TrackingV2GetOrderQuery['order']['address'],
  onAddOverlays: (overlays: any[]) => void
): GetAnnotationsResult => {
  const annotations = []

  if (!cachedDriverAnnotation) {
    cachedDriverAnnotation = buildDriverAnnotation(
      mapkit,
      new mapkit.Coordinate(driverLocationWithTransition.lat, driverLocationWithTransition.lng),
      {
        title: 'Repartidor'
      }
    )
  } else {
    cachedDriverAnnotation.coordinate = new mapkit.Coordinate(
      driverLocationWithTransition.lat,
      driverLocationWithTransition.lng
    )

    const element = cachedDriverAnnotation.element
    element.style.transform = `rotate(${calculateDriverRotation(
      driverLocationWithTransition,
      driverLocation
    )}deg)`
  }

  if (driverLocationWithTransition?.lat && driverLocationWithTransition?.lng) {
    annotations.push(cachedDriverAnnotation)
  }

  if (address?.location?.lat && address?.location?.lng) {
    annotations.push(getHomeAnnotation(mapkit, address))
  }

  if (annotations.length === 2) {
    getRoute(
      mapkit,
      driverLocation,
      address.location,
      {
        strokeColor: STROKE_COLOR,
        lineWidth: 5
      },
      onAddOverlays
    )
  }

  return {annotations, type: 'shortRange', overlays: []}
}
